import React, { useState, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import {
  useCreateShiftChangeRequestMutation,
  CreateShiftChangeRequestInput,
  useGetShiftChangeRequestSuggestionEmployeesQuery,
} from '../../../../../__generated__/graphql';
import { useMutationErrorHandler } from '../../../../../../../hooks/useMutationErrorHandler';
import { SelectSingle } from '../../../../../components/Selects/SelectSingle';
import { useToasts } from '../../../../../../../context/Toast';
import Loading from './Loading';
import './TransferShiftModal.css';

type TransferShiftModalProps = {
  show: boolean;
  onClose: () => void;
  shiftId: string;
  onSuccess?: () => void;
};

export const TransferShiftModal: React.FC<
  React.PropsWithChildren<TransferShiftModalProps>
> = ({ show, onClose, shiftId, onSuccess }) => {
  const t = useNamespacedTranslation('schedulerShifts.transferShiftModal');
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: colleaguesData, loading: loadingColleagues } =
    useGetShiftChangeRequestSuggestionEmployeesQuery();

  const employeeOptions = useMemo(
    () =>
      colleaguesData?.me.allColleagues.map((colleague) => ({
        value: colleague.Id,
        label: colleague.Name,
      })) ?? [],
    [colleaguesData],
  );

  const [createShiftChangeRequest] = useCreateShiftChangeRequestMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!employeeId) {
      errorHandler(new Error(t('employeeIdRequired')));
      return;
    }

    setIsSubmitting(true);
    try {
      const input: CreateShiftChangeRequestInput = {
        shiftId,
        proposedResourceEmployeeId: employeeId,
        changeReason: changeReason.trim(),
      };

      const result = await createShiftChangeRequest({
        variables: { input },
      });

      if (result.errors) {
        throw new Error(result.errors[0]?.message || t('submitError'));
      }

      if (result.data?.shiftChangeRequest?.createShiftChangeRequest?.recordId) {
        addSuccess(t('submitSuccess'));
        setEmployeeId(null);
        setChangeReason('');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmployeeId(null);
      setChangeReason('');
      onClose();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="modal-90"
      backdrop={true}
      backdropClassName="transfer-shift-modal-backdrop"
      className="transfer-shift-modal"
      container={document.body}
      enforceFocus={false}>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>{t('title')}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {loadingColleagues ? (
            <Loading />
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('employeeLabel')}
                  <span className="text-danger"> *</span>
                </Form.Label>
                <SelectSingle
                  name="employee"
                  placeholder={t('employeeLabel')}
                  options={employeeOptions}
                  value={employeeId}
                  onChange={(value) => {
                    setEmployeeId(value);
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('reasonLabel')}
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder={t('reasonPlaceholder')}
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}>
            {t('cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('submitting') : t('submit')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
